import {
  BrainBackfillPauseReason,
  BrainBackfillStatus,
  BrainDiscoveryCounts,
  BrainDiscoveryErrorCode,
  BrainDiscoveryHierarchyMode,
  BrainDiscoveryTruncation,
  BrainEdgeStatus,
  BrainEntityType,
  BrainReviewMode,
  BrainSignalDefinitionStatus,
  BrainSignalEngine,
  BrainSignalLevel,
  BrainSignalTemplateArea,
  BrainSourceType,
} from '@this+that/models';

import { BrainAskSuggestionKind } from './utils/brainAskSuggestions';
import { BrainEdgeTone, BrainNeighbourGroup } from './utils/brainEntityGraph';
import { BrainEntityDisplayStatus } from './utils/brainEntityTree';

export const BRAIN_AUTO_CREATE_OPTION = '__brain_auto_create__';
export const BRAIN_NO_PARENT_OPTION = '__brain_no_parent__';
export const BRAIN_ALL_AREAS_OPTION = '__brain_all_areas__';
export const BRAIN_ENTITIES_TO_CONFIRM_LIMIT = 50;
export const BRAIN_ASK_MAX_LENGTH = 500;
export const BRAIN_BACKFILL_POLL_INTERVAL_MS = 15_000;
export const BRAIN_DISCOVERY_POLL_INTERVAL_MS = 5_000;
export const BRAIN_ENTITIES_TO_CONFIRM_ANCHOR = 'brain-entities-to-confirm';
export const BRAIN_OPEN_SIGNALS_LIMIT = 20;

export const BRAIN_SIGNAL_LEVEL_KEY: Record<BrainSignalLevel, string> = {
  [BrainSignalLevel.NONE]: 'brain.signals.level.none',
  [BrainSignalLevel.LOW]: 'brain.signals.level.low',
  [BrainSignalLevel.MODERATE]: 'brain.signals.level.moderate',
  [BrainSignalLevel.HIGH]: 'brain.signals.level.high',
  [BrainSignalLevel.CRITICAL]: 'brain.signals.level.critical',
};

export const BRAIN_SIGNAL_LEVEL_CLASS: Record<BrainSignalLevel, string> = {
  [BrainSignalLevel.NONE]:
    'bg-semantic-bg-elevated text-semantic-text-secondary',
  [BrainSignalLevel.LOW]:
    'bg-semantic-bg-elevated text-semantic-text-secondary',
  [BrainSignalLevel.MODERATE]:
    'bg-semantic-bg-elevated text-semantic-text-warning',
  [BrainSignalLevel.HIGH]:
    'bg-semantic-bg-warning-subtle text-semantic-text-warning',
  [BrainSignalLevel.CRITICAL]:
    'bg-semantic-bg-danger-subtle text-semantic-text-danger',
};

export const BRAIN_SIGNAL_AREA_ORDER: readonly BrainSignalTemplateArea[] = [
  BrainSignalTemplateArea.SALES,
  BrainSignalTemplateArea.PRODUCT,
  BrainSignalTemplateArea.ENGINEERING,
  BrainSignalTemplateArea.GENERAL,
];

export const BRAIN_SIGNAL_AREA_KEY: Record<BrainSignalTemplateArea, string> = {
  [BrainSignalTemplateArea.SALES]: 'brain.signals.areas.SALES',
  [BrainSignalTemplateArea.PRODUCT]: 'brain.signals.areas.PRODUCT',
  [BrainSignalTemplateArea.ENGINEERING]: 'brain.signals.areas.ENGINEERING',
  [BrainSignalTemplateArea.GENERAL]: 'brain.signals.areas.GENERAL',
};

export const BRAIN_SIGNAL_NOTIFY_LEVELS: readonly BrainSignalLevel[] = [
  BrainSignalLevel.LOW,
  BrainSignalLevel.MODERATE,
  BrainSignalLevel.HIGH,
  BrainSignalLevel.CRITICAL,
];

export const BRAIN_SIGNAL_MAX_COOLDOWN_HOURS = 24 * 90;

export const BRAIN_SIGNAL_DEFINITION_STATUS_KEY: Record<
  BrainSignalDefinitionStatus,
  string
> = {
  [BrainSignalDefinitionStatus.ACTIVE]: 'brain.signals.definitionStatus.active',
  [BrainSignalDefinitionStatus.PAUSED]: 'brain.signals.definitionStatus.paused',
  [BrainSignalDefinitionStatus.ARCHIVED]:
    'brain.signals.definitionStatus.archived',
};

export const BRAIN_SIGNAL_ENGINE_KEY: Record<BrainSignalEngine, string> = {
  [BrainSignalEngine.RULES]: 'brain.signals.engine.rules',
  [BrainSignalEngine.HAIKU]: 'brain.signals.engine.haiku',
  [BrainSignalEngine.SONNET]: 'brain.signals.engine.sonnet',
  [BrainSignalEngine.JEV]: 'brain.signals.engine.jev',
};

export const BRAIN_ENTITY_TYPE_KEY: Record<BrainEntityType, string> = {
  [BrainEntityType.PERSON]: 'brain.blueprint.aliases.type.person',
  [BrainEntityType.PROJECT]: 'brain.blueprint.aliases.type.project',
  [BrainEntityType.CUSTOMER]: 'brain.blueprint.aliases.type.customer',
  [BrainEntityType.TOPIC]: 'brain.blueprint.aliases.type.topic',
  [BrainEntityType.SYSTEM]: 'brain.blueprint.aliases.type.system',
  [BrainEntityType.TEAM]: 'brain.blueprint.aliases.type.team',
};

export const BRAIN_ENTITY_STATUS_KEY: Record<BrainEntityDisplayStatus, string> =
  {
    [BrainEntityDisplayStatus.DECLARED]:
      'brain.blueprint.entities.status.declared',
    [BrainEntityDisplayStatus.DISCOVERED]:
      'brain.blueprint.entities.status.discovered',
    [BrainEntityDisplayStatus.TO_CONFIRM]:
      'brain.blueprint.entities.status.toConfirm',
  };

export const BRAIN_ENTITY_STATUS_CLASS: Record<
  BrainEntityDisplayStatus,
  string
> = {
  [BrainEntityDisplayStatus.DECLARED]:
    'bg-semantic-bg-badge text-semantic-text-accent',
  [BrainEntityDisplayStatus.DISCOVERED]:
    'bg-semantic-bg-info-subtle text-semantic-text-info',
  [BrainEntityDisplayStatus.TO_CONFIRM]:
    'bg-semantic-bg-warning-subtle text-semantic-text-warning',
};

export const BRAIN_NEIGHBOUR_GROUP_KEY: Record<BrainNeighbourGroup, string> = {
  [BrainNeighbourGroup.WORKERS]: 'brain.graph.relation.workers',
  [BrainNeighbourGroup.OWNERS]: 'brain.graph.relation.owners',
  [BrainNeighbourGroup.PART_OF]: 'brain.graph.relation.partOf',
  [BrainNeighbourGroup.CONTAINS]: 'brain.graph.relation.contains',
  [BrainNeighbourGroup.WORKS_ON]: 'brain.graph.relation.worksOn',
  [BrainNeighbourGroup.OWNS]: 'brain.graph.relation.owns',
  [BrainNeighbourGroup.REPORTS_TO]: 'brain.graph.relation.reportsTo',
  [BrainNeighbourGroup.DIRECT_REPORTS]: 'brain.graph.relation.directReports',
  [BrainNeighbourGroup.CHANNELS]: 'brain.graph.relation.channels',
};

export const BRAIN_EDGE_STATUS_KEY: Record<BrainEdgeStatus, string> = {
  [BrainEdgeStatus.ACTIVE]: 'brain.graph.status.active',
  [BrainEdgeStatus.SUPERSEDED]: 'brain.graph.status.superseded',
  [BrainEdgeStatus.DISPUTED]: 'brain.graph.status.disputed',
  [BrainEdgeStatus.NEEDS_CONFIRMATION]: 'brain.graph.status.needsConfirmation',
};

export const BRAIN_EDGE_TONE_CLASS: Record<BrainEdgeTone, string> = {
  [BrainEdgeTone.ACTIVE]: 'bg-semantic-bg-badge text-semantic-text-accent',
  [BrainEdgeTone.WARNING]:
    'bg-semantic-bg-warning-subtle text-semantic-text-warning',
  [BrainEdgeTone.MUTED]: 'bg-semantic-bg-elevated text-semantic-text-tertiary',
};

export {
  BRAIN_PERSON_MEMBERSHIP_CLASS,
  BRAIN_PERSON_MEMBERSHIP_KEY,
} from '../../../../../../../components/Brain/CompanyBrain/brainPersonPresentation';

export const BRAIN_BACKFILL_STATUS_KEY: Record<BrainBackfillStatus, string> = {
  [BrainBackfillStatus.ESTIMATED]: 'brain.blueprint.backfill.status.estimated',
  [BrainBackfillStatus.RUNNING]: 'brain.blueprint.backfill.status.running',
  [BrainBackfillStatus.PAUSED]: 'brain.blueprint.backfill.status.paused',
  [BrainBackfillStatus.DONE]: 'brain.blueprint.backfill.status.done',
  [BrainBackfillStatus.FAILED]: 'brain.blueprint.backfill.status.failed',
};

export const BRAIN_BACKFILL_PAUSE_REASON_KEY: Record<
  BrainBackfillPauseReason,
  string
> = {
  [BrainBackfillPauseReason.CAP_REACHED]:
    'brain.blueprint.backfill.pauseReason.capReached',
  [BrainBackfillPauseReason.PAUSED_BY_USER]:
    'brain.blueprint.backfill.pauseReason.pausedByUser',
  [BrainBackfillPauseReason.ENQUEUE_FAILED]:
    'brain.blueprint.backfill.pauseReason.enqueueFailed',
};

export const BRAIN_BACKFILL_SOURCE_KEY: Record<BrainSourceType, string> = {
  [BrainSourceType.SLACK]: 'brain.blueprint.backfill.source.slack',
  [BrainSourceType.EMAIL]: 'brain.blueprint.backfill.source.email',
};

export const BRAIN_REVIEW_MODE_ORDER: readonly BrainReviewMode[] = [
  BrainReviewMode.AUTO_PUBLISH_CONFIDENT,
  BrainReviewMode.REVIEW_ALL,
];

export const BRAIN_REVIEW_MODE_KEY: Record<
  BrainReviewMode,
  { title: string; description: string }
> = {
  [BrainReviewMode.AUTO_PUBLISH_CONFIDENT]: {
    title: 'brain.blueprint.reviewMode.autoPublishConfident.title',
    description: 'brain.blueprint.reviewMode.autoPublishConfident.description',
  },
  [BrainReviewMode.REVIEW_ALL]: {
    title: 'brain.blueprint.reviewMode.reviewAll.title',
    description: 'brain.blueprint.reviewMode.reviewAll.description',
  },
};

export const BRAIN_DISCOVERY_ERROR_KEY: Record<
  BrainDiscoveryErrorCode,
  string
> = {
  [BrainDiscoveryErrorCode.TOO_MANY_ATTEMPTS]:
    'brain.blueprint.discovery.errors.tooManyAttempts',
  [BrainDiscoveryErrorCode.ENQUEUE_FAILED]:
    'brain.blueprint.discovery.errors.enqueueFailed',
  [BrainDiscoveryErrorCode.PIPELINE_FAILED]:
    'brain.blueprint.discovery.errors.pipelineFailed',
  [BrainDiscoveryErrorCode.NO_MESSAGES]:
    'brain.blueprint.discovery.errors.noMessages',
  [BrainDiscoveryErrorCode.DRAFT_EXISTS]:
    'brain.blueprint.discovery.errors.draftExists',
  [BrainDiscoveryErrorCode.STALE]: 'brain.blueprint.discovery.errors.stale',
};

export const BRAIN_DISCOVERY_UNKNOWN_ERROR_KEY =
  'brain.blueprint.discovery.errors.unknown';

export const BRAIN_DISCOVERY_START_ERROR_KEY = {
  FORBIDDEN: 'brain.blueprint.discovery.errors.forbidden',
  INVALID_WINDOW: 'brain.blueprint.discovery.errors.invalidWindow',
  BLUEPRINT_EXISTS: 'brain.blueprint.discovery.errors.blueprintExists',
  IN_PROGRESS: 'brain.blueprint.discovery.errors.inProgress',
  START_FAILED: 'brain.blueprint.discovery.errors.startFailed',
  NO_CALENDAR: 'brain.blueprint.discovery.errors.noCalendar',
} as const;

export const BRAIN_DISCOVERY_NO_CALENDAR_ACTION_KEY =
  'brain.blueprint.discovery.errors.noCalendarAction';

type BrainDiscoveryNumericCountKey = {
  [K in keyof BrainDiscoveryCounts]-?: NonNullable<
    BrainDiscoveryCounts[K]
  > extends number
    ? K
    : never;
}[keyof BrainDiscoveryCounts];

export type BrainDiscoveryCountField = Exclude<
  BrainDiscoveryNumericCountKey,
  'lowConfidence'
>;

/** `model` placed every name, so it says nothing. */
export const BRAIN_DISCOVERY_HIERARCHY_NOTE_KEY: Readonly<
  Partial<Record<BrainDiscoveryHierarchyMode, string>>
> = {
  [BrainDiscoveryHierarchyMode.FALLBACK]:
    'brain.blueprint.discovery.hierarchyFallback',
  [BrainDiscoveryHierarchyMode.PARTIAL]:
    'brain.blueprint.discovery.hierarchyPartial',
};

/** MESSAGE_CAP is a plural key: it takes `count` (messages read). */
export const BRAIN_DISCOVERY_TRUNCATION_KEY: Readonly<
  Record<BrainDiscoveryTruncation, string>
> = {
  [BrainDiscoveryTruncation.MESSAGE_CAP]:
    'brain.blueprint.discovery.truncation.messageCap',
  [BrainDiscoveryTruncation.TEXT_BUDGET]:
    'brain.blueprint.discovery.truncation.textBudget',
  [BrainDiscoveryTruncation.TIME]: 'brain.blueprint.discovery.truncation.time',
};

export const BRAIN_DISCOVERY_TRUNCATION_FALLBACK_KEY =
  'brain.blueprint.discovery.truncated';

/** Plural keys: each takes `count` and resolves through `_one` / `_other`. */
export const BRAIN_DISCOVERY_COUNT_KEY: Record<
  BrainDiscoveryCountField,
  string
> = {
  messages: 'brain.blueprint.discovery.counts.messages',
  members: 'brain.blueprint.discovery.counts.members',
  threads: 'brain.blueprint.discovery.counts.threads',
  areas: 'brain.blueprint.discovery.counts.areas',
  entities: 'brain.blueprint.discovery.counts.entities',
  peopleCount: 'brain.blueprint.discovery.counts.people',
  edgeCount: 'brain.blueprint.discovery.counts.edges',
  meetings: 'brain.blueprint.discovery.counts.meetings',
};

/** Graph counts are absent on older runs and say nothing at zero, so they show only when positive. */
export const BRAIN_DISCOVERY_POSITIVE_ONLY_COUNTS: ReadonlySet<BrainDiscoveryCountField> =
  new Set<BrainDiscoveryCountField>(['peopleCount', 'edgeCount', 'meetings']);

export const BRAIN_PANEL_TEST_IDS = {
  PANEL: 'brain-panel',
  EMPTY_STATE: 'brain-panel-empty-state',
  START_SETUP: 'brain-panel-start-setup',
  SAVE_DRAFT: 'brain-panel-save-draft',
  ACTIVATE: 'brain-panel-activate',
  CONFIRM_ACTIVATE: 'brain-panel-confirm-activate',
  ADD_AREA: 'brain-panel-add-area',
  ADD_ALIAS: 'brain-panel-add-alias',
  VERSION_ROW: 'brain-panel-version-row',
  STATS: 'brain-panel-stats',
  CONFLICT: 'brain-panel-conflict',
  READ_ONLY_SUMMARY: 'brain-panel-read-only-summary',
  ENTITIES: 'brain-panel-entities',
  ASK_FORM: 'brain-panel-ask-form',
  ASK_INPUT: 'brain-panel-ask-input',
  ASK_CHIP: 'brain-panel-ask-chip',
  ENTITY_ROW: 'brain-panel-entity-row',
  ENTITY_NEIGHBOURS: 'brain-panel-entity-neighbours',
  NEIGHBOUR_ROW: 'brain-panel-neighbour-row',
  PEOPLE: 'brain-panel-people',
  PERSON_ROW: 'brain-panel-person-row',
  ENTITY_TO_CONFIRM_ROW: 'brain-panel-entity-to-confirm-row',
  CONFIRM_ENTITY: 'brain-panel-confirm-entity',
  MERGE_ENTITY: 'brain-panel-merge-entity',
  CONFIRM_ENTITY_SUBMIT: 'brain-panel-confirm-entity-submit',
  MERGE_ENTITY_SUBMIT: 'brain-panel-merge-entity-submit',
  BACKFILL: 'brain-panel-backfill',
  OPEN_BACKFILL: 'brain-panel-open-backfill',
  BACKFILL_ESTIMATE: 'brain-panel-backfill-estimate',
  BACKFILL_START: 'brain-panel-backfill-start',
  BACKFILL_PROGRESS: 'brain-panel-backfill-progress',
  BACKFILL_PLAN_ROW: 'brain-panel-backfill-plan-row',
  SKIPPED_TODAY: 'brain-panel-skipped-today',
  DISCOVER: 'brain-panel-discover',
  DISCOVERY_PROGRESS: 'brain-panel-discovery-progress',
  DISCOVERY_RETRY: 'brain-panel-discovery-retry',
  DISCOVERY_BANNER: 'brain-panel-discovery-banner',
  DISCOVERY_TRUNCATION_NOTE: 'brain-panel-discovery-truncation-note',
  DISCOVERY_HIERARCHY_NOTE: 'brain-panel-discovery-hierarchy-note',
  DISCOVERY_REMOVE_CHECKED: 'brain-panel-discovery-remove-checked',
  DISCOVERY_SOURCES: 'brain-panel-discovery-sources',
  DISCOVERY_SOURCE_CHIPS: 'brain-panel-discovery-source-chips',
  LOW_CONFIDENCE_BADGE: 'brain-panel-low-confidence-badge',
  DISCOVERY_CALENDAR: 'brain-panel-discovery-calendar',
  DISCOVERY_GRAPH_LINE: 'brain-panel-discovery-graph-line',
  HISTORY_LOAD_STARTED: 'brain-panel-history-load-started',
  AREA_MEMBERS: 'brain-panel-area-members',
  SUGGESTED_EDGE: 'brain-panel-suggested-edge',
  CONFIRM_EDGE: 'brain-panel-confirm-edge',
  DISMISS_EDGE: 'brain-panel-dismiss-edge',
  COWORKERS_SUMMARY: 'brain-panel-coworkers-summary',
  COWORKERS: 'brain-panel-coworkers',
  COWORKER: 'brain-panel-coworker',
  COWORKER_EVIDENCE: 'brain-panel-coworker-evidence',
  COWORKER_DIRECT_REPORTS: 'brain-panel-coworker-direct-reports',
  COWORKERS_UNASSIGNED: 'brain-panel-coworkers-unassigned',
  COWORKER_KEEP: 'brain-panel-coworker-keep',
  COWORKER_REMOVE: 'brain-panel-coworker-remove',
  COWORKER_INVITE: 'brain-panel-coworker-invite',
  PERSON_MEMBERSHIP: 'brain-panel-person-membership',
  REPORTS_TO: 'brain-panel-reports-to',
  CONFIRM_REPORTS_TO: 'brain-panel-confirm-reports-to',
  DISMISS_REPORTS_TO: 'brain-panel-dismiss-reports-to',
  SIGNALS: 'brain-panel-signals',
  SIGNAL_TEMPLATE: 'brain-panel-signal-template',
  SIGNAL_ACTIVATE: 'brain-panel-signal-activate',
  SIGNAL_CONDITION_TOGGLE: 'brain-panel-signal-condition-toggle',
  SIGNAL_CONDITION: 'brain-panel-signal-condition',
  SIGNAL_PAUSE: 'brain-panel-signal-pause',
  SIGNAL_RESUME: 'brain-panel-signal-resume',
  SIGNAL_ROW: 'brain-panel-signal-row',
  SIGNAL_LINK: 'brain-panel-signal-link',
  SIGNAL_LEVEL: 'brain-panel-signal-level',
  SIGNAL_AREA: 'brain-panel-signal-area',
  SIGNAL_POLICY_TOGGLE: 'brain-panel-signal-policy-toggle',
  SIGNAL_POLICY_FORM: 'brain-panel-signal-policy-form',
  SIGNAL_POLICY_REVIEW: 'brain-panel-signal-policy-review',
  SIGNAL_POLICY_EMAIL: 'brain-panel-signal-policy-email',
  SIGNAL_POLICY_MIN_LEVEL: 'brain-panel-signal-policy-min-level',
  SIGNAL_POLICY_COOLDOWN: 'brain-panel-signal-policy-cooldown',
  SIGNAL_POLICY_SAVE: 'brain-panel-signal-policy-save',
  SIGNAL_POLICY_CANCEL: 'brain-panel-signal-policy-cancel',
  SIGNAL_POLICY_ERROR: 'brain-panel-signal-policy-error',
  SUGGESTED_SIGNALS: 'brain-panel-suggested-signals',
  SUGGESTED_SIGNAL: 'brain-panel-suggested-signal',
  SUGGESTED_SIGNAL_REASON: 'brain-panel-suggested-signal-reason',
  SUGGESTED_SIGNAL_STATUS: 'brain-panel-suggested-signal-status',
  SUGGESTED_SIGNAL_KEEP: 'brain-panel-suggested-signal-keep',
  SUGGESTED_SIGNAL_REMOVE: 'brain-panel-suggested-signal-remove',
} as const;

export const BRAIN_PANEL_STYLES = {
  input:
    'w-full bg-semantic-bg-input border border-semantic-border-input rounded-semantic px-3 py-2 text-sm leading-5 text-semantic-text-primary placeholder:text-semantic-text-tertiary outline-none focus:border-semantic-border-accent disabled:opacity-60',
  numberInput:
    'w-28 bg-semantic-bg-input border border-semantic-border-input rounded-semantic px-3 py-2 text-sm leading-5 text-semantic-text-primary outline-none focus:border-semantic-border-accent disabled:opacity-60',
  fieldLabel:
    'text-xs font-medium uppercase tracking-wide leading-4 text-semantic-text-secondary',
  helper: 'text-xs leading-4 text-semantic-text-tertiary',
  primaryButton:
    'inline-flex items-center justify-center gap-2 bg-semantic-bg-accent hover:bg-semantic-bg-accent-hover text-semantic-emerald-fg text-sm font-bold px-4 py-2.5 rounded-semantic border-none cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
  secondaryButton:
    'inline-flex items-center justify-center gap-2 bg-transparent border border-semantic-border-input text-semantic-text-secondary text-sm font-semibold px-4 py-2.5 rounded-semantic cursor-pointer hover:bg-semantic-bg-hover hover:text-semantic-text-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
  linkButton:
    'inline-flex items-center gap-1.5 self-start text-sm font-semibold text-semantic-text-accent hover:underline bg-transparent border-none cursor-pointer p-0 disabled:opacity-60 disabled:cursor-not-allowed',
  iconButton:
    'inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-semantic text-semantic-text-tertiary hover:text-semantic-text-danger hover:bg-semantic-bg-danger-hover bg-transparent border-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed',
} as const;

export const BRAIN_ASK_SUGGESTION_KEY: Record<BrainAskSuggestionKind, string> =
  {
    [BrainAskSuggestionKind.DECIDED]: 'brain.ask.suggestion.decided',
    [BrainAskSuggestionKind.OWNER]: 'brain.ask.suggestion.owner',
    [BrainAskSuggestionKind.BLOCKED]: 'brain.ask.suggestion.blocked',
  };
